import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Plus } from 'lucide-react';
import { useMyBotsQuery, useProjectBotsQuery } from '../../hooks/queries';
import { useBots } from '../../contexts/BotsContext';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import BotAvatar, { botState } from './BotAvatar';
import BotName from './BotName';
import { useBotLabel } from './botDisplay';

/** The bots that can work in a project: its team's bots and the person's own,
 * the ones already working there first (the same set as the project's chat). */
export const useProjectEligibleBots = (project) => {
  const { data: mine } = useMyBotsQuery();
  const { data: inProject } = useProjectBotsQuery(project?.id);
  const inNoProject = !project;
  return useMemo(() => {
    const joined = new Set((inProject?.bots || []).map((b) => b.bot?.id));
    // Outside projects (a chat in no project) every bot of the person can talk.
    const fits = (mine?.bots || []).filter((b) => (inNoProject ? true : b.owner_type === 'team'
      ? b.owner_id === project?.team_id : b.owner_type === 'user'));
    return [...fits.filter((b) => joined.has(b.id)), ...fits.filter((b) => !joined.has(b.id))];
  }, [mine, inProject, project?.team_id, inNoProject]);
};

/**
 * Who answers: a bot is chosen in the composer, like a model in Grok or
 * ChatGPT, from the bots of the project the chat is in; outside projects from
 * BusyBee and every bot of the person. The one place the bot of a chat is
 * changed. With onOpenSheet the chip opens the phone's bot sheet instead of a menu.
 */
const ComposerBotPicker = ({ bot, project, onPick, onOpenSheet, disabled, className = '' }) => {
  const { t } = useTranslation();
  const botLabel = useBotLabel();
  const { openGallery } = useBots();
  const eligible = useProjectEligibleBots(project);
  const { data: mine } = useMyBotsQuery();
  // BusyBee's chats are in no project, so she is offered outside projects only.
  const bots = useMemo(() => (!project && mine?.queen ? [mine.queen, ...eligible] : eligible), [project, mine, eligible]);
  const current = [...(mine?.queen ? [mine.queen] : []), ...(mine?.bots || [])].find((b) => b.id === bot?.id) || bot;
  const status = (b) => {
    const state = botState(b);
    if (state === 'needs_you') return { text: t('bots.hub.waiting', { count: b.pending_approvals }), tone: 'text-orange-600 dark:text-attention-text' };
    if (state === 'working') return { text: t('bots.hub.working'), tone: 'text-muted-foreground' };
    return { text: t('bots.hub.idle'), tone: 'text-muted-foreground' };
  };
  const chip = (
    <button type="button" disabled={disabled} onClick={onOpenSheet} data-testid="composer-bot-picker"
      aria-label={t('bots.chat.choose_bot')} title={t('bots.chat.choose_bot')}
      className={`inline-flex items-center gap-1.5 h-8 max-w-[200px] pl-1 pr-2 rounded-full border border-border text-xs text-foreground hover:bg-accent disabled:opacity-50 transition-colors ${className}`}>
      <BotAvatar bot={current} size="sm" />
      <span className="truncate font-medium">{current ? botLabel(current) : ''}</span>
      <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
    </button>
  );
  if (onOpenSheet) return chip;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{chip}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" className="w-[300px]">
        <DropdownMenuLabel className="text-[11px] font-medium text-muted-foreground">
          {t(project ? 'bots.chat.bots_in_project' : 'bots.chat.choose_bot')}
        </DropdownMenuLabel>
        {bots.map((b) => {
          const s = status(b);
          return (
            <DropdownMenuItem key={b.id} onSelect={() => b.id !== current?.id && onPick(b.id)} className="gap-2.5 py-2"
              data-testid="composer-bot-option">
              <BotAvatar bot={b} size={32} state={botState(b)} />
              <span className="flex-1 min-w-0 flex flex-col">
                <BotName bot={b} className="text-sm truncate" />
                <span className={`text-xs ${s.tone}`}>{s.text}</span>
              </span>
              {b.id === current?.id && <Check className="w-4 h-4 text-foreground shrink-0" />}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => openGallery({})} className="gap-2.5 text-muted-foreground">
          <Plus className="w-4 h-4" />{t('bots.add_bot')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ComposerBotPicker;
